import type { IExtractParams, IRoutePath } from "../types";

import type { IAppointmentPageType } from "@pages/panelPages/appointment/types";
import { generatePath } from "@routes/generatePath.ts";
import type { IPatientType } from "@/constants";
import { flatten } from "@/utils";

const appointmentListRoute = "/appointments-list/:appt_type";
const createAppointment = "/appointments-list/:appt_type/create";
const viewAppointment = "/appointments-list/:appt_type/:appt_id/view";
const rescheduleAppointment =
  "/appointments-list/:appt_type/:appt_id/reschedule";

export type ICreateAppointmentParams = IExtractParams<
  typeof createAppointment,
  IAppointmentPageType
>;

export enum ICreateAppointmentSearchParamsEnum {
  doctorId = "doctorId",
  selectedTime = "selectedTime",
  patientId = "patientId",
  patientType = "patientType",
  patientFullName = "patientFullName",
  scheduleId = "scheduleId",
  index = "index",
  facilityId = "facilityId",
}

export interface ICreateAppointmentSearchParams {
  [ICreateAppointmentSearchParamsEnum.doctorId]?: string;
  [ICreateAppointmentSearchParamsEnum.facilityId]?: string;
  selectedTimeObjInfo?: {
    /**
     * in ISO format. (e.g. 2024-03-18T10:16:00.000000Z)
     */
    [ICreateAppointmentSearchParamsEnum.selectedTime]?: string;
    [ICreateAppointmentSearchParamsEnum.index]?: string;
    [ICreateAppointmentSearchParamsEnum.scheduleId]?: string;
  };

  patientInfo?: {
    [ICreateAppointmentSearchParamsEnum.patientId]: string;
    [ICreateAppointmentSearchParamsEnum.patientType]: IPatientType;
    [ICreateAppointmentSearchParamsEnum.patientFullName]: string;
  };
}

export type IViewAppointmentParam = IExtractParams<
  typeof viewAppointment,
  string
>;
export type IRescheduleAppointmentParam = IExtractParams<
  typeof rescheduleAppointment,
  string
>;

export type IApptType = IAppointmentPageType | "all" | "no-show";

export const path = {
  list: {
    route: appointmentListRoute,
    link: (type: IApptType) => {
      return generatePath(appointmentListRoute, { appt_type: type });
    },
  },
  create: {
    route: createAppointment,
    link(
      type: IAppointmentPageType,
      searchParams?: ICreateAppointmentSearchParams,
    ) {
      return generatePath(
        createAppointment,
        { appt_type: type },
        flatten(searchParams),
      );
    },
  },
  view: {
    route: viewAppointment,
    link(id: string, apptType: IApptType) {
      return generatePath(viewAppointment, {
        appt_id: id,
        appt_type: apptType,
      });
    },
  },
  reschedule: {
    route: rescheduleAppointment,
    link(id: string, apptType: IApptType) {
      return generatePath(rescheduleAppointment, {
        appt_id: id,
        appt_type: apptType,
      });
    },
  },
} satisfies IRoutePath;
